import React, { useEffect, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Button,
  FormControl,
  FormHelperText,
  FormLabel,
  Heading,
  Input,
  Link,
  Stack,
  Text,
} from '@chakra-ui/react';
import { Link as RouterLink, useNavigate, useSearchParams } from 'react-router-dom';
import { apiForgotPassword, apiResetPassword } from '../api/client';
import PortalAuthShell from './PortalAuthShell';

export default function ForgotPasswordPage() {
  const RESEND_COOLDOWN_SECONDS = 30;
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState(searchParams.get('email') || '');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [resendSecondsLeft, setResendSecondsLeft] = useState(0);

  useEffect(() => {
    if (resendSecondsLeft <= 0) {
      return undefined;
    }

    const timerId = window.setTimeout(() => {
      setResendSecondsLeft(current => current - 1);
    }, 1000);

    return () => window.clearTimeout(timerId);
  }, [resendSecondsLeft]);

  const requestOtp = async () => {
    const normalizedEmail = email.trim();
    const result: any = await apiForgotPassword(normalizedEmail);

    if (result?.success) {
      setOtpSent(true);
      setResendSecondsLeft(RESEND_COOLDOWN_SECONDS);
      setSuccessMessage(result.message || 'Check your email for the OTP.');
      return { success: true };
    }

    setError(result?.message || 'Unable to send the OTP right now.');
    return { success: false };
  };

  const handleRequestOtp = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    try {
      setSubmitting(true);
      await requestOtp();
    } finally {
      setSubmitting(false);
    }
  };

  const handleResendOtp = async () => {
    setError('');
    setSuccessMessage('');

    try {
      setSubmitting(true);
      const result = await requestOtp();

      if (result.success) {
        setOtp('');
        setSuccessMessage('A new OTP has been sent. Check your email.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!otp.trim()) {
      setError('OTP is required.');
      return;
    }

    if (password.trim().length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    try {
      setSubmitting(true);

      const result: any = await apiResetPassword(email.trim(), otp.trim(), password);

      if (result?.success) {
        navigate('/member/login', {
          replace: true,
          state: {
            message: 'Password reset successful. Please log in with your new password.',
          },
        });
        return;
      }

      setError(result?.message || 'Unable to reset your password right now.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PortalAuthShell
      title="Reset your password"
      subtitle="Enter your email, receive an OTP, then set a new password."
    >
      <Stack as="form" spacing={5} onSubmit={otpSent ? handleResetPassword : handleRequestOtp}>
        <Stack spacing={2}>
          <Heading size="lg">Forgot Password</Heading>
          <Text color="gray.600">
            {otpSent
              ? 'Check your email, enter the OTP, and choose a new password.'
              : 'Enter your email first. If the account exists, the backend will verify it and email an OTP.'}
          </Text>
        </Stack>

        {successMessage ? (
          <Alert status="success" borderRadius="lg">
            <AlertIcon />
            {successMessage}
          </Alert>
        ) : null}

        {error ? (
          <Alert status="error" borderRadius="lg">
            <AlertIcon />
            {error}
          </Alert>
        ) : null}

        <FormControl isRequired>
          <FormLabel>Email</FormLabel>
          <Input
            type="email"
            placeholder="name@example.com"
            value={email}
            onChange={e => setEmail(e.target.value)}
            size="lg"
            isReadOnly={otpSent}
          />
          <FormHelperText>
            {otpSent ? 'OTP was requested for this email address.' : 'The OTP will be sent to this email address.'}
          </FormHelperText>
        </FormControl>

        {otpSent ? (
          <>
            <FormControl isRequired>
              <FormLabel>OTP</FormLabel>
              <Input
                type="text"
                placeholder="Enter the OTP from your email"
                value={otp}
                onChange={e => setOtp(e.target.value)}
                size="lg"
                inputMode="numeric"
              />
              <FormHelperText>Use the OTP sent to your email address.</FormHelperText>
            </FormControl>

            <FormControl isRequired>
              <FormLabel>New Password</FormLabel>
              <Input
                type="password"
                placeholder="Minimum 6 characters"
                value={password}
                onChange={e => setPassword(e.target.value)}
                size="lg"
                autoComplete="new-password"
              />
            </FormControl>

            <FormControl isRequired>
              <FormLabel>Confirm Password</FormLabel>
              <Input
                type="password"
                placeholder="Re-enter your new password"
                value={confirmPassword}
                onChange={e => setConfirmPassword(e.target.value)}
                size="lg"
                autoComplete="new-password"
              />
            </FormControl>

            <Button type="submit" colorScheme="blue" size="lg" isLoading={submitting}>
              Reset password
            </Button>

            <Button
              variant="ghost"
              size="lg"
              onClick={handleResendOtp}
              isDisabled={submitting || resendSecondsLeft > 0}
            >
              {resendSecondsLeft > 0 ? `Resend OTP in ${resendSecondsLeft}s` : 'Resend OTP'}
            </Button>

            <Button
              variant="outline"
              size="lg"
              onClick={() => {
                setOtp('');
                setPassword('');
                setConfirmPassword('');
                setSuccessMessage('');
                setError('');
                setOtpSent(false);
                setResendSecondsLeft(0);
              }}
              isDisabled={submitting}
            >
              Use another email
            </Button>
          </>
        ) : (
          <Button type="submit" colorScheme="blue" size="lg" isLoading={submitting}>
            Send OTP
          </Button>
        )}

        <Text color="gray.600">
          Remembered it?{' '}
          <Link as={RouterLink} to="/member/login" color="blue.600" fontWeight="600">
            Back to login
          </Link>
        </Text>
      </Stack>
    </PortalAuthShell>
  );
}